import React from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Users, ListChecks, FolderOpen, Trophy, LineChart, ArrowLeft, LogOut, FileText, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/applications", label: "Applications", icon: FileText },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/questions", label: "Questions", icon: ListChecks },
  { to: "/admin/categories", label: "Categories", icon: FolderOpen },
  { to: "/admin/attempts", label: "Exam Attempts", icon: Trophy },
  { to: "/admin/analytics", label: "Analytics", icon: LineChart },
  { to: "/admin/content", label: "Landing Page", icon: PenLine },
];

export default function AdminLayout({ children, title, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/categories" replace />;

  return (
    <div className="min-h-screen bg-[#0f0e0c] text-[#F5F1EA] flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-white/8 bg-black/40 flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="p-6 border-b border-white/8">
          <Link to="/admin" className="flex items-center gap-2.5" data-testid="admin-brand">
            <img src="/godrive-mark.png" alt="Go Drive TVDE" className="h-8 w-auto" />
            <div className="leading-tight">
              <div className="font-serif font-semibold text-sm">Go Drive TVDE</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[hsl(var(--accent))]">Admin</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);
            return (
              <Link key={item.to} to={item.to} data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  active ? "bg-[hsl(var(--accent))] text-black font-medium" : "text-white/70 hover:bg-white/8 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" />{item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/8 space-y-1">
          <button onClick={() => nav("/categories")} data-testid="admin-back-to-app"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/60 hover:bg-white/8 hover:text-white w-full">
            <ArrowLeft className="w-4 h-4" /> Back to app
          </button>
          <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/60 hover:bg-white/8 hover:text-white w-full">
            <LogOut className="w-4 h-4" /> Log out
          </button>
          <div className="text-[10px] font-mono text-white/40 px-3 pt-2 truncate">{user.email}</div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="border-b border-white/8 bg-black/20 sticky top-0 z-10 backdrop-blur-md">
          <div className="px-8 py-4 flex items-center justify-between">
            <h1 className="font-serif text-2xl font-medium" data-testid="admin-page-title">{title}</h1>
            <div className="flex items-center gap-2">{actions}</div>
          </div>
        </div>
        <div className="p-8">{children}</div>
      </main>
    </div>
  );
}
